<i18n
  src="@/locales/views/packing_house/process/exec_steps/edit_exec_step.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/process_line_exec_step.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/process_line_exec_input.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/process_line_exec_output.yml" lang="yaml"></i18n>
<i18n src="@/locales/models/process_line_exec_attr.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="this.routeName('show_exec_step')"
    :params="{
      packing_house_id: this.packing_house_id,
      packing_house_process_id: this.packing_house_process_id,
      process_line_step_id: this.process_line_step_id,
      id: this.id,
    }"
  ></go-back-link>

  <bread-crumbs
    :labels="[module_name, packing_house_process.name, process_line_step.name]"
  ></bread-crumbs>

  <alert-danger :errors_data="std_err" />

  <div class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div
          class="space-y-8 divide-y divide-gray-200"
          v-if="load_general_data"
        >
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.process_line_exec_step.executed_at") }}
              </label>
              <div class="mt-1">
                <date-form
                  v-model="process_line_exec_step.executed_at"
                  :timezone="undefined"
                  mode="datetime"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.process_line_exec_step.finished_at") }}
              </label>
              <div class="mt-1">
                <date-form
                  v-model="process_line_exec_step.finished_at"
                  :timezone="undefined"
                  mode="datetime"
                />
              </div>
            </div>
            <div class="sm:col-span-3" v-if="process_lines.length > 0">
              <label class="label-form">
                {{ $t("model.process_line_exec_step.process_line") }}
              </label>
              <div class="mt-1">
                <select-form
                  :options="process_lines"
                  v-model="process_line_exec_step.process_line_id"
                  :is_invalid="isInvalidSelect('process_line')"
                />
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-4">
            <div class="col-span-1 sm:col-span-6">
              <label class="label-form">
                {{ $t("model.process_line_exec_step.observations") }}
              </label>
              <div class="mt-1">
                <textarea
                  v-model="process_line_exec_step.observations"
                  rows="3"
                  :class="['input-text-area', isInvalid('observations')]"
                />
              </div>
            </div>
          </div>

          <div class="pt-4">
            <h3 class="col-span-2">{{ $t("labels.exec_inputs") }}</h3>
            <div class="divide-y divide-gray-200">
              <div
                v-for="(
                  exec_input, index
                ) in process_line_exec_step.process_line_exec_inputs"
                :key="index"
                class="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4 py-4"
              >
                <div
                  v-if="
                    !exec_input._destroy &&
                    process_line_step.process_line_input_products.length > 1
                  "
                  class="col-span-2 grid justify-end"
                >
                  <white-button
                    @click.prevent="removeExecInput(index)"
                    label="X"
                  />
                </div>
                <ExecStepInputForm
                  v-if="!exec_input._destroy"
                  :packing_house_id="packing_house_id"
                  :packing_house_process_id="packing_house_process_id"
                  :process_line_step="process_line_step"
                  :process_line_input_products="
                    process_line_step.process_line_input_products
                  "
                  v-model="
                    process_line_exec_step.process_line_exec_inputs[index]
                  "
                />
              </div>
            </div>
            <div
              v-if="process_line_step.process_line_input_products.length > 1"
              class="flex justify-end"
            >
              <white-button
                @click.prevent="addExecInput"
                :label="$t('labels.add_exec_input_button')"
              />
            </div>
          </div>

          <div class="pt-4">
            <h3 class="col-span-2">{{ $t("labels.exec_outputs") }}</h3>
            <div class="divide-y divide-gray-200">
              <div
                v-for="(
                  exec_output, index
                ) in process_line_exec_step.process_line_exec_outputs"
                :key="index"
                class="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4 py-4"
              >
                <div
                  v-if="
                    !exec_output._destroy &&
                    process_line_step.process_line_output_products.length > 1
                  "
                  class="col-span-2 grid justify-end"
                >
                  <white-button
                    @click.prevent="removeExecOutput(index)"
                    label="X"
                  />
                </div>
                <ExecStepOutputForm
                  v-if="!exec_output._destroy"
                  :packing_house_id="packing_house_id"
                  :packing_house_process_id="packing_house_process_id"
                  :process_line_output_products="
                    process_line_step.process_line_output_products
                  "
                  v-model="
                    process_line_exec_step.process_line_exec_outputs[index]
                  "
                />
              </div>
            </div>
            <div
              v-if="process_line_step.process_line_output_products.length > 1"
              class="flex justify-end"
            >
              <white-button
                @click.prevent="addExecOutput"
                :label="$t('labels.add_exec_output_button')"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowExecStep"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!charging_submit"
            @click.prevent="update()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Requester from "@/views/packing_house/requesters/process/exec_steps/new_exec_step.js";
import ExecStepInputForm from "@/components/views/packing_house/process/exec_steps/ExecStepInputForm.vue";
import ExecStepOutputForm from "@/components/views/packing_house/process/exec_steps/ExecStepOutputForm.vue";
export default {
  name: "PHProcessExecStepsNewExecStep",
  components: {
    ExecStepInputForm,
    ExecStepOutputForm,
  },
  data() {
    return {
      load_general_data: false,
      packing_house_process: {},
      process_line_step: {},
      process_lines: [],
      process_line_exec_step: {},
      charging_submit: false,
    };
  },
  props: {
    packing_house_id: {
      required: true,
    },
    packing_house_process_id: {
      required: true,
    },
    process_line_step_id: {
      required: true,
    },
    id: {
      required: true,
    },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          packing_house_id: this.packing_house_id,
        },
      }).then(() => {
        this.loadData();
      })
    );
  },
  methods: {
    loadData: async function () {
      this.fetchPackingHouseProcess();
      await this.fetchProcessLineStep();
      await this.fetchProcessLines();
      await this.fetchProcessLineExecStep();
      this.load_general_data = true;
    },

    fetchPackingHouseProcess: async function () {
      let response = await Requester.fetchPackingHouseProcess(
        this.packing_house_id,
        this.packing_house_process_id
      );
      this.packing_house_process = response.data;
    },

    fetchProcessLineStep: async function () {
      let response = await Requester.fetchProcessLineStep(
        this.packing_house_id,
        this.process_line_step_id
      );
      this.process_line_step = response.data;
    },

    fetchProcessLines: async function () {
      let response = await Requester.fetchProcessLines(
        this.packing_house_id,
        this.packing_house_process_id
      );
      this.process_lines = response.data;
    },

    fetchProcessLineExecStep: async function () {
      let response = await Requester.fetchProcessLineExecStep(
        this.packing_house_id,
        this.id
      );
      this.process_line_exec_step = response.data;
      this.process_line_exec_step.process_line_exec_inputs.forEach(
        (exec_input) => {
          exec_input["exported_batch"] = exec_input.exported_batch ?? {};
        }
      );
    },

    addExecInput() {
      this.process_line_exec_step.process_line_exec_inputs.push({
        process_line_product_id: null,
        value: "",
        unit: "",
        harvested_batch: {},
        _destroy: false,
      });
    },

    removeExecInput(index) {
      if (this.process_line_exec_step.process_line_exec_inputs[index].id) {
        this.process_line_exec_step.process_line_exec_inputs[
          index
        ]._destroy = true;
      } else {
        this.process_line_exec_step.process_line_exec_inputs.splice(index, 1);
      }
    },

    addExecOutput() {
      this.process_line_exec_step.process_line_exec_outputs.push({
        process_line_product_id: null,
        value: "",
        unit: "",
        exported_batch: {},
        _destroy: false,
      });
    },

    removeExecOutput(index) {
      if (this.process_line_exec_step.process_line_exec_outputs[index].id) {
        this.process_line_exec_step.process_line_exec_outputs[
          index
        ]._destroy = true;
      } else {
        this.process_line_exec_step.process_line_exec_outputs.splice(index, 1);
      }
    },

    update: async function () {
      this.charging_submit = true;
      try {
        const response = await Requester.update(
          this.packing_house_id,
          this.id,
          this.process_line_exec_step
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowExecStep();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      } finally {
        this.charging_submit = false;
      }
    },

    goToShowExecStep: function () {
      this.$router.push({
        name: this.routeName("show_exec_step"),
        params: {
          packing_house_id: this.packing_house_id,
          packing_house_process_id: this.packing_house_process_id,
          process_line_step_id: this.process_line_step_id,
        },
      });
    },
  },
};
</script>
